import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getMergedSiteContent } from '../firebase/tenants';
import { updateTenant } from '../firebase/tenants';

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function SiteContentEditor({ tenant, tenantId, onSaved }) {
  const [content, setContent] = useState(() => getMergedSiteContent(tenant));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const aboutImageInputRef = useRef(null);
  const trainerImageInputRef = useRef(null);
  const [trainerUploadIndex, setTrainerUploadIndex] = useState(null);

  useEffect(() => {
    setContent(getMergedSiteContent(tenant));
  }, [tenant?.id, tenant?.siteContent]);

  const updateSection = (section, field, value) => {
    setContent((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value
      }
    }));
  };

  const updateNested = (section, subKey, field, value) => {
    setContent((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [subKey]: {
          ...(prev[section]?.[subKey] || {}),
          [field]: value
        }
      }
    }));
  };

  const setSectionJson = (section, raw) => {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw || '[]') : raw;
      setContent((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), ...parsed } }));
    } catch (_) {
      // invalid JSON, ignore
    }
  };

  const updateTrainerImage = (index, base64DataUrl) => {
    setContent((prev) => {
      const list = Array.isArray(prev.trainers?.list) ? [...prev.trainers.list] : [];
      if (index < 0 || index >= list.length) return prev;
      list[index] = { ...list[index], imageUrl: base64DataUrl };
      return {
        ...prev,
        trainers: { ...prev.trainers, list }
      };
    });
  };

  const updateTrainerField = (index, field, value) => {
    setContent((prev) => {
      const list = Array.isArray(prev.trainers?.list) ? [...prev.trainers.list] : [];
      if (index < 0 || index >= list.length) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, trainers: { ...prev.trainers, list } };
    });
  };

  const addTrainer = () => {
    setContent((prev) => {
      const list = Array.isArray(prev.trainers?.list) ? [...prev.trainers.list] : [];
      list.push({ name: '', specialty: 'Trainer', imageUrl: '' });
      return { ...prev, trainers: { ...prev.trainers, list } };
    });
  };

  const removeTrainer = (index) => {
    setContent((prev) => {
      const list = Array.isArray(prev.trainers?.list) ? [...prev.trainers.list] : [];
      if (index < 0 || index >= list.length) return prev;
      list.splice(index, 1);
      return { ...prev, trainers: { ...prev.trainers, list } };
    });
  };

  const handleAboutImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const dataUrl = await readFileAsBase64(file);
      updateSection('about', 'imageUrl', dataUrl);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to read image. Try a different file.' });
    }
    e.target.value = '';
  };

  const handleTrainerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      setTrainerUploadIndex(null);
      return;
    }
    const index = trainerUploadIndex;
    setTrainerUploadIndex(null);
    if (index == null) return;
    try {
      const dataUrl = await readFileAsBase64(file);
      updateTrainerImage(index, dataUrl);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to read image. Try a different file.' });
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setMessage(null);
    try {
      const result = await updateTenant(tenantId, { siteContent: content });
      if (result.success) {
        setMessage({
          type: 'success',
          text: `Site content saved to Firebase: tenants / ${tenantId} / siteContent. Refresh your public site to see changes.`
        });
        onSaved?.();
      } else {
        const detail = result.error ? ` (${result.error})` : '';
        setMessage({ type: 'error', text: (result.message || 'Failed to save') + detail });
      }
    } catch (err) {
      const code = err.code || err.name || '';
      const msg = err.message || 'Failed to save';
      setMessage({ type: 'error', text: `${msg}${code ? ` [${code}]` : ''}. Check Firebase Console → Realtime Database → tenants → ${tenantId} → siteContent.` });
    } finally {
      setSaving(false);
    }
  };

  const h = (content?.hero || {});
  const t = (content?.timings || {});
  const a = (content?.about || {});
  const c = (content?.classes || {});
  const cta = (content?.cta || {});
  const tr = (content?.trainers || {});
  const trainerList = Array.isArray(tr.list) ? tr.list : [];
  const p = (content?.pricing || {});
  const bt = (content?.becomeTrainer || {});
  const co = (content?.contact || {});

  return (
    <div className="dashboard-content">
      <h2 className="dashboard-title">Website content</h2>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Edit the content shown on your public gym website. Changes apply after you save.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Saved in Firebase Realtime Database under: <strong>tenants → {tenantId || '…'} → siteContent</strong>. Click &quot;Save site content&quot; to write; use small images (&lt;~500KB) to avoid write limits.
      </Typography>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Hero (top section)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Tagline"
              value={h.tagline || ''}
              onChange={(e) => updateSection('hero', 'tagline', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField label="Title line 1" value={h.titleLine1 || ''} onChange={(e) => updateSection('hero', 'titleLine1', e.target.value)} fullWidth size="small" />
            <TextField label="Title line 2" value={h.titleLine2 || ''} onChange={(e) => updateSection('hero', 'titleLine2', e.target.value)} fullWidth size="small" />
            <TextField
              label="Description lines (JSON array of strings)"
              value={JSON.stringify(h.descriptionLines || [], null, 2)}
              onChange={(e) => {
                try {
                  const arr = JSON.parse(e.target.value || '[]');
                  if (Array.isArray(arr)) updateSection('hero', 'descriptionLines', arr);
                } catch (_) {}
              }}
              fullWidth
              multiline
              minRows={2}
              size="small"
              helperText={'e.g. ["Line 1", "Line 2"]'}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Gym timings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Gym type" value={t.gymType || ''} onChange={(e) => updateSection('timings', 'gymType', e.target.value)} fullWidth size="small" placeholder="(UNISEX GYM)" />
            <TextField label="Subtitle" value={t.subtitle || ''} onChange={(e) => updateSection('timings', 'subtitle', e.target.value)} fullWidth size="small" />
            <TextField
              label="Slots (JSON array: { period, type, typeClass })"
              value={JSON.stringify(t.slots || [], null, 2)}
              onChange={(e) => {
                try {
                  const arr = JSON.parse(e.target.value || '[]');
                  if (Array.isArray(arr)) updateSection('timings', 'slots', arr);
                } catch (_) {}
              }}
              fullWidth
              multiline
              minRows={4}
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>About section</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Tag" value={a.tag || ''} onChange={(e) => updateSection('about', 'tag', e.target.value)} fullWidth size="small" />
            <TextField label="Title" value={a.title || ''} onChange={(e) => updateSection('about', 'title', e.target.value)} fullWidth size="small" />
            <TextField label="Description" value={a.description || ''} onChange={(e) => updateSection('about', 'description', e.target.value)} fullWidth multiline size="small" />
            <Typography variant="subtitle2">Stat 1</Typography>
            <Box display="flex" gap={1}>
              <TextField label="Number" value={a.stat1?.number || ''} onChange={(e) => updateNested('about', 'stat1', 'number', e.target.value)} size="small" />
              <TextField label="Suffix" value={a.stat1?.suffix || ''} onChange={(e) => updateNested('about', 'stat1', 'suffix', e.target.value)} size="small" />
              <TextField label="Label" value={a.stat1?.label || ''} onChange={(e) => updateNested('about', 'stat1', 'label', e.target.value)} size="small" fullWidth />
            </Box>
            <Typography variant="subtitle2">Stat 2</Typography>
            <Box display="flex" gap={1}>
              <TextField label="Number" value={a.stat2?.number || ''} onChange={(e) => updateNested('about', 'stat2', 'number', e.target.value)} size="small" />
              <TextField label="Suffix" value={a.stat2?.suffix || ''} onChange={(e) => updateNested('about', 'stat2', 'suffix', e.target.value)} size="small" />
              <TextField label="Label" value={a.stat2?.label || ''} onChange={(e) => updateNested('about', 'stat2', 'label', e.target.value)} size="small" fullWidth />
            </Box>
            <TextField label="Second paragraph" value={a.secondParagraph || ''} onChange={(e) => updateSection('about', 'secondParagraph', e.target.value)} fullWidth multiline size="small" />
            <Typography variant="subtitle2">About image (URL or upload from device – uploads saved as base64)</Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {(!a.imageUrl || !a.imageUrl.startsWith('data:')) && (
                <TextField
                  label="Image URL"
                  value={a.imageUrl || ''}
                  onChange={(e) => updateSection('about', 'imageUrl', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Paste image URL, or upload below"
                />
              )}
              {a.imageUrl?.startsWith('data:') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">Uploaded image (base64)</Typography>
                  <Button size="small" onClick={() => updateSection('about', 'imageUrl', '')}>Clear</Button>
                </Box>
              )}
              <input
                ref={aboutImageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAboutImageUpload}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<ImageIcon />}
                onClick={() => aboutImageInputRef.current?.click()}
              >
                {a.imageUrl?.startsWith('data:') ? 'Replace with new image' : 'Upload image (saved as base64)'}
              </Button>
              {a.imageUrl && (a.imageUrl.startsWith('data:') || a.imageUrl.startsWith('http')) && (
                <Box sx={{ mt: 1 }}>
                  <img src={a.imageUrl} alt="About" style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 8 }} />
                </Box>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Classes</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Subtitle suffix" value={c.subtitleSuffix || ''} onChange={(e) => updateSection('classes', 'subtitleSuffix', e.target.value)} fullWidth size="small" />
            <TextField label="Title" value={c.title || ''} onChange={(e) => updateSection('classes', 'title', e.target.value)} fullWidth size="small" />
            <TextField label="Description" value={c.description || ''} onChange={(e) => updateSection('classes', 'description', e.target.value)} fullWidth multiline size="small" />
            <TextField
              label="Items (JSON: [{ icon, title, description }])"
              value={JSON.stringify(c.items || [], null, 2)}
              onChange={(e) => {
                try {
                  const arr = JSON.parse(e.target.value || '[]');
                  if (Array.isArray(arr)) updateSection('classes', 'items', arr);
                } catch (_) {}
              }}
              fullWidth
              multiline
              minRows={3}
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Call to action</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Title" value={cta.title || ''} onChange={(e) => updateSection('cta', 'title', e.target.value)} fullWidth size="small" />
            <TextField label="Button text" value={cta.buttonText || ''} onChange={(e) => updateSection('cta', 'buttonText', e.target.value)} fullWidth size="small" />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Trainers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Subtitle suffix" value={tr.subtitleSuffix || ''} onChange={(e) => updateSection('trainers', 'subtitleSuffix', e.target.value)} fullWidth size="small" />
            <TextField label="Title" value={tr.title || ''} onChange={(e) => updateSection('trainers', 'title', e.target.value)} fullWidth size="small" />
            <TextField label="Description" value={tr.description || ''} onChange={(e) => updateSection('trainers', 'description', e.target.value)} fullWidth multiline size="small" />
            <Typography variant="subtitle2">Trainers list</Typography>
            <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={addTrainer} sx={{ alignSelf: 'flex-start' }}>
              Add trainer
            </Button>
            {trainerList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No trainers yet. Click &quot;Add trainer&quot; above, then fill name, specialty and optionally upload a photo. Remember to Save at the bottom.</Typography>
            ) : (
              <>
                <Box display="flex" flexWrap="wrap" gap={2}>
                  {trainerList.map((trainer, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        minWidth: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 1
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">Trainer {idx + 1}</Typography>
                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => removeTrainer(idx)}>
                          Delete
                        </Button>
                      </Box>
                      {trainer.imageUrl && (trainer.imageUrl.startsWith('data:') || trainer.imageUrl.startsWith('http')) ? (
                        <img src={trainer.imageUrl} alt={trainer.name} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, alignSelf: 'center' }} />
                      ) : (
                        <Box sx={{ width: 80, height: 80, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center' }}>
                          <ImageIcon sx={{ color: 'text.secondary' }} />
                        </Box>
                      )}
                      <TextField
                        label="Name"
                        value={trainer.name || ''}
                        onChange={(e) => updateTrainerField(idx, 'name', e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="Trainer name"
                      />
                      <TextField
                        label="Specialty"
                        value={trainer.specialty || ''}
                        onChange={(e) => updateTrainerField(idx, 'specialty', e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="e.g. Trainer"
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={() => {
                          setTrainerUploadIndex(idx);
                          setTimeout(() => trainerImageInputRef.current?.click(), 0);
                        }}
                      >
                        Upload photo
                      </Button>
                    </Box>
                  ))}
                </Box>
                <input
                  ref={trainerImageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleTrainerImageUpload}
                />
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Pricing</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Title" value={p.title || ''} onChange={(e) => updateSection('pricing', 'title', e.target.value)} fullWidth size="small" />
            <TextField label="Description" value={p.description || ''} onChange={(e) => updateSection('pricing', 'description', e.target.value)} fullWidth multiline size="small" />
            <TextField label="Payment info" value={p.paymentInfo || ''} onChange={(e) => updateSection('pricing', 'paymentInfo', e.target.value)} fullWidth size="small" />
            <TextField
              label="Categories (JSON: [{ title, plans: [{ name, badge, featured? }] }])"
              value={JSON.stringify(p.categories || [], null, 2)}
              onChange={(e) => {
                try {
                  const arr = JSON.parse(e.target.value || '[]');
                  if (Array.isArray(arr)) updateSection('pricing', 'categories', arr);
                } catch (_) {}
              }}
              fullWidth
              multiline
              minRows={6}
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Become a trainer</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Title" value={bt.title || ''} onChange={(e) => updateSection('becomeTrainer', 'title', e.target.value)} fullWidth size="small" />
            <TextField label="Description" value={bt.description || ''} onChange={(e) => updateSection('becomeTrainer', 'description', e.target.value)} fullWidth multiline size="small" />
            <TextField label="Button text" value={bt.buttonText || ''} onChange={(e) => updateSection('becomeTrainer', 'buttonText', e.target.value)} fullWidth size="small" />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Contact</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Heading" value={co.heading || ''} onChange={(e) => updateSection('contact', 'heading', e.target.value)} fullWidth size="small" />
            <TextField label="Subtitle" value={co.subtitle || ''} onChange={(e) => updateSection('contact', 'subtitle', e.target.value)} fullWidth size="small" />
            <TextField label="Address title" value={co.addressTitle || ''} onChange={(e) => updateSection('contact', 'addressTitle', e.target.value)} fullWidth size="small" />
            <TextField
              label="Address lines (JSON array of strings)"
              value={JSON.stringify(co.addressLines || [], null, 2)}
              onChange={(e) => {
                try {
                  const arr = JSON.parse(e.target.value || '[]');
                  if (Array.isArray(arr)) updateSection('contact', 'addressLines', arr);
                } catch (_) {}
              }}
              fullWidth
              multiline
              minRows={2}
              size="small"
            />
            <TextField label="Phone" value={co.phone || ''} onChange={(e) => updateSection('contact', 'phone', e.target.value)} fullWidth size="small" />
            <TextField label="Email" value={co.email || ''} onChange={(e) => updateSection('contact', 'email', e.target.value)} fullWidth size="small" />
            <TextField label="Hours" value={co.hours || ''} onChange={(e) => updateSection('contact', 'hours', e.target.value)} fullWidth size="small" />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !tenantId}
        >
          {saving ? 'Saving...' : 'Save site content'}
        </Button>
        {tenant?.slug && (
          <Button variant="outlined" href={`/gym/${tenant.slug}`} target="_blank" rel="noopener noreferrer">
            View public site
          </Button>
        )}
      </Box>
    </div>
  );
}

export default SiteContentEditor;
